import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Check, X, Clock, MessageSquare, UserCheck } from 'lucide-react';
import { matchingApi } from '../../api';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const ConnectionsList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await matchingApi.getConnections();
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConnection = async (connectionId, status) => {
    setUpdating(connectionId);
    try {
      await matchingApi.updateConnection(connectionId, status);
      fetchConnections();
    } catch (error) {
      console.error('Failed to update connection:', error);
    } finally {
      setUpdating(null);
    }
  };

  const getFilteredConnections = () => {
    switch (activeTab) {
      case 'pending':
        return connections.filter(c => c.status === 'pending' && c.receiver_id === user?.id);
      case 'sent':
        return connections.filter(c => c.status === 'pending' && c.requester_id === user?.id);
      case 'accepted':
        return connections.filter(c => c.status === 'accepted');
      default:
        return connections;
    }
  };

  const filteredConnections = getFilteredConnections();
  const pendingCount = connections.filter(c => c.status === 'pending' && c.receiver_id === user?.id).length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-48 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-500" />
          <h3 className="font-semibold text-gray-900">{t('connections.my_connections')}</h3>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { key: 'all', label: t('connections.tab_all') },
          { key: 'pending', label: t('connections.tab_requests'), count: pendingCount },
          { key: 'sent', label: t('connections.tab_sent') },
          { key: 'accepted', label: t('connections.tab_connected') },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === tab.key
                ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Connections List */}
      {filteredConnections.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{t('connections.no_connections')}</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {filteredConnections.map((connection) => {
            const otherUser = connection.other_user;
            const isPending = connection.status === 'pending';
            const isReceiver = connection.receiver_id === user?.id;
            
            return (
              <div key={connection.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4">
                  <img
                    src={otherUser?.avatar || `https://ui-avatars.com/api/?name=${otherUser?.name || 'User'}&background=10b981&color=fff`}
                    alt={otherUser?.name}
                    className="w-12 h-12 rounded-full"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{otherUser?.name || 'Unknown'}</h4>
                      {connection.status === 'accepted' && (
                        <UserCheck className="w-4 h-4 text-emerald-500" />
                      )}
                    </div>
                    {otherUser?.bio && (
                      <p className="text-sm text-gray-500 truncate">{otherUser.bio}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        connection.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                        connection.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {connection.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {connection.connection_type?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {isPending && isReceiver ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleUpdateConnection(connection.id, 'accepted')}
                          disabled={updating === connection.id}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateConnection(connection.id, 'rejected')}
                          disabled={updating === connection.id}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : isPending && !isReceiver ? (
                      <span className="flex items-center gap-1 text-sm text-yellow-600">
                        <Clock className="w-4 h-4" />
                        Pending
                      </span>
                    ) : connection.status === 'accepted' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const otherUserId = otherUser?.id
                            || (connection.requester_id === user?.id ? connection.receiver_id : connection.requester_id);
                          if (otherUserId) navigate(`/dashboard/messages?with=${otherUserId}`);
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Message
                      </Button>
                    ) : null}
                  </div>
                </div>
                
                {connection.message && isPending && isReceiver && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 italic">
                    &quot;{connection.message}&quot;
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectionsList;
